import { must } from "@/common/utils/must";
import * as styleUtils from "@/common/utils/styleUtils";
import type ExperienceData from "@/modules/ExperienceData";
import { loadButtonTraits } from "@/modules/styleButtonTraits";
import { readableOn } from "@/modules/styleColors";
import {
  addFontFaceEntry,
  addSelectorValue,
  addStyle,
  type CssMap,
  cssSize,
} from "@/modules/styleCss";
import { loadChainStyles, loadPanelTraits } from "@/modules/stylePanelTraits";
import { loadTextTraits } from "@/modules/styleTextTraits";
import type {
  ExperienceStyle,
  StyleFontSpec,
  StyleIcon,
  StyleSheetSpec,
  StyleTraits,
} from "@/types/experience";

const DYNAMIC_STYLESHEET = "pix-dynamic-styles-34532";

const links: string[] = [];

function loadStylesheets(
  document: Document,
  css: CssMap,
  sources: StyleSheetSpec[],
) {
  for (let i = 0, max = sources.length; i < max; i++) {
    const source = sources[i];
    const url = source.url;
    if (url != null) {
      const id = `pix-dynamic-stylesheet-${source.idstylesheet}`;
      source.id = id;
      // css file
      addStylesheetLink(document, { url: url, id: id, media: source.media });
    }
  }
}

function loadFonts(document: Document, css: CssMap, sources: StyleFontSpec[]) {
  for (let i = 0, max = sources.length; i < max; i++) {
    const source = sources[i];
    // @font-face spec
    addFontFace(css, source);
  }
}

function addStylesheetLink(
  document: Document,
  spec: { url: string; id: string; media?: string },
) {
  // add the link and save for later removal.
  if (styleUtils.addStylesheetLink(document, spec)) {
    links.push(spec.id);
  }
}

function addFontLocal(target: string, name: string): string {
  return `${target + (target.length > 0 ? "," : "")}local(${name})`;
}

function addFontSrc(target: string, source: string, type: string): string {
  return (
    target +
    (target.length > 0 ? "," : "") +
    "url(" +
    source +
    ") format('" +
    type +
    "')"
  );
}

function getFontSources(spec: StyleFontSpec): string[] {
  const src = [];

  if (spec.eotUrl) {
    src.push(`url('${spec.eotUrl}')`);
  }

  let s2 = "";
  if (spec.local1) {
    s2 = addFontLocal(s2, spec.local1);
  }
  if (spec.local2) {
    s2 = addFontLocal(s2, spec.local2);
  }
  if (spec.eotUrl) {
    s2 = addFontSrc(s2, spec.eotUrl, "eot");
  }
  if (spec.woffUrl) {
    s2 = addFontSrc(s2, spec.woffUrl, "woff");
  }
  if (spec.woff2Url) {
    s2 = addFontSrc(s2, spec.woff2Url, "woff2");
  }
  if (spec.ttfUrl) {
    s2 = addFontSrc(s2, spec.ttfUrl, "truetype");
  }
  if (spec.svgUrl) {
    s2 = addFontSrc(s2, spec.svgUrl, "svg");
  }
  if (s2.length > 0) {
    src.push(s2);
  }

  return src;
}

function addFontFace(css: CssMap, spec: StyleFontSpec) {
  const faceCss = {};

  addSelectorValue(faceCss, "font-family", spec.family);
  const sources = getFontSources(spec);
  for (let i = 0, max = sources.length; i < max; i++) {
    addSelectorValue(faceCss, "src", sources[i]);
  }
  addSelectorValue(faceCss, "unicode-range", spec.unicodeRange);
  addSelectorValue(faceCss, "font-variant", spec.variant);
  addSelectorValue(faceCss, "font-feature-settings", spec.featureSettings);
  addSelectorValue(faceCss, "font-stretch", spec.stretch);
  addSelectorValue(faceCss, "font-weight", spec.weight);
  addSelectorValue(faceCss, "font-style", spec.style);
  addFontFaceEntry(css, faceCss);
}

function loadTraits(
  target: string,
  document: Document,
  css: CssMap,
  traits: StyleTraits,
) {
  loadTextTraits(target, css, traits);
  loadButtonTraits(target, css, traits);
  loadPanelTraits(target, document, css, traits);
  loadChainStyles(target, css, traits);
}

function loadColors(
  document: Document,
  css: CssMap,
  colors: NonNullable<ExperienceStyle["colors"]>,
) {
  const target = "all";
  addStyle(
    target,
    css,
    ".highlight-bg-1",
    "background-color",
    colors.highlight1,
  );
  addStyle(
    target,
    css,
    ".swiper-pagination-bullet-active",
    "background-color",
    colors.highlight1,
  );
  addStyle(target, css, ".highlight-fg-1", "color", colors.highlight1);
  // The user chat bubble is filled with highlight1, which can be
  // light (e.g. beige) or dark (e.g. navy) per brand. Pair it with a legible
  // text colour computed from the fill's luminance, so the message never
  // renders white-on-light or dark-on-dark. This 2-class selector beats the
  // advertiser `.content` colour the bubble also carries.
  if (colors.highlight1) {
    addStyle(
      target,
      css,
      ".ask-message--user .ask-message-bubble",
      "color",
      readableOn(colors.highlight1),
    );
  }
}

function loadLexicon(
  document: Document,
  css: CssMap,
  lexicon: NonNullable<ExperienceStyle["lexicon"]>,
) {
  const target = "all";

  if (lexicon.saved) {
    addStyle(
      target,
      css,
      ".add-favorite-btn.selected > span:after",
      "content",
      `'${lexicon.saved}'`,
    );
  }

  if (lexicon.save) {
    addStyle(
      target,
      css,
      ".add-favorite-btn > span:after",
      "content",
      `'${lexicon.save}'`,
    );
  }

  if (lexicon.cta) {
    // moved to labels and implemented in the associated component
  }

  if (lexicon.hintTitle) {
    addStyle(
      target,
      css,
      ".pixmoto-play.landscape .hint-title:after",
      "content",
      `'${lexicon.hintTitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.landscape.cta .hint-title:after",
      "content",
      `'${lexicon.hintTitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.portrait .hint-title:after",
      "content",
      `'${lexicon.hintTitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.portrait.cta .hint-title:after",
      "content",
      `'${lexicon.hintTitle}'`,
    );
  }

  if (lexicon.hintSubtitle) {
    addStyle(
      target,
      css,
      ".pixmoto-play.landscape .hint-subtitle:after",
      "content",
      `'${lexicon.hintSubtitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.landscape.cta .hint-subtitle:after",
      "content",
      `'${lexicon.hintSubtitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.portrait .hint-subtitle:after",
      "content",
      `'${lexicon.hintSubtitle}'`,
    );
    addStyle(
      target,
      css,
      ".pixmoto-play.portrait.cta .hint-subtitle:after",
      "content",
      `'${lexicon.hintSubtitle}'`,
    );
  }
}

function loadIcons(document: Document, css: CssMap, icons: StyleIcon[]) {
  const target = "all";
  const container = document.getElementById("svg-icons");
  const svg = container?.getElementsByTagName("svg")[0];
  const doctype =
    '<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">';

  const parser = new DOMParser();

  for (let n = 0, max = icons.length; n < max; n++) {
    const icon = icons[n];
    const name = `icon-${icon.name}`;
    const className = `.${name}-d`;

    const sprite = must(svg, "#svg-icons sprite");
    const existing = sprite.getElementById(name);
    const svgdoc = parser.parseFromString(doctype + icon.data, "image/svg+xml");
    const svgnode = svgdoc.documentElement;

    if (existing) {
      sprite.insertBefore(svgnode, existing);
      sprite.removeChild(existing);
    } else {
      sprite.appendChild(svgnode);
    }

    addStyle(target, css, className, "width", cssSize(icon.width));
    addStyle(target, css, className, "height", cssSize(icon.height));
  }
}

export function clearStyles(document: Document) {
  styleUtils.removeStylesheet(document, DYNAMIC_STYLESHEET);
  styleUtils.removeStylesheetLinks(document, links);
  links.length = 0;
}

/*
 * Sets up common styles like highlight colors, fonts, borders, etc
 * based on the style portion of the experienceData
 */
export function loadStyles(document: Document, experienceData: ExperienceData) {
  clearStyles(document);

  if (experienceData.style) {
    const css = {};

    if (experienceData.style.stylesheets) {
      loadStylesheets(document, css, experienceData.style.stylesheets);
    }

    if (experienceData.style.fonts) {
      loadFonts(document, css, experienceData.style.fonts);
    }

    const traits = experienceData.style.traits;
    if (traits) {
      Object.keys(traits).forEach(target => {
        loadTraits(target, document, css, traits[target]);
      });
    }

    if (experienceData.style.colors) {
      loadColors(document, css, experienceData.style.colors);
    }

    if (experienceData.style.lexicon) {
      loadLexicon(document, css, experienceData.style.lexicon);
    }

    if (experienceData.style.icons) {
      loadIcons(document, css, experienceData.style.icons);
    }

    if (Object.keys(css).length > 0) {
      const stylesheet = styleUtils.createStylesheet(
        document,
        DYNAMIC_STYLESHEET,
        css,
      );
      document.body.appendChild(stylesheet);
    }
  }
}
